import { CHATWOOT_BASE, authHeaders } from '@/api/chatwoot'
import type { Contact, AccountLabel } from '@/types/contacts'

const ACCOUNT_PATH = `${CHATWOOT_BASE}/api/v1/accounts/3`

function jsonHeaders(token: string) {
  return { 'Content-Type': 'application/json', ...authHeaders(token) }
}

/**
 * Cache em memória por (token, busca, página). TTL curto: cobre o caso comum de ir e voltar
 * entre páginas já vistas (não deveria refazer o fetch), sem servir dado velho por muito
 * tempo. Invalidado nas mutações (create/update/delete/import), e ignorado quando `force`
 * é passado (usado pelo botão "Atualizar" da toolbar).
 */
const pageCache = new Map<string, { data: { contacts: Contact[]; total: number }; ts: number }>()
const PAGE_CACHE_TTL_MS = 30_000

function pageCacheKey(token: string, page: number, search: string): string {
  return `${token}::${search}::${page}`
}

/** Exportado só pra reset de estado de módulo entre testes (ver contactsService.test.ts). */
export function invalidatePageCache() {
  pageCache.clear()
}

/**
 * Busca uma única página de contatos do backend (paginação de servidor de verdade).
 * Sem `search`, lista a conta inteira; com `search`, usa o endpoint de busca do Chatwoot
 * (ILIKE em nome/e-mail/telefone) — que não normaliza formatação de telefone, ver
 * `isPhoneLikeQuery`/`fetchAllContactsParallelApi` pra busca por telefone.
 */
export async function fetchContactsPageApi(
  token: string, page: number, search: string, opts: { signal?: AbortSignal; force?: boolean } = {}
): Promise<{ contacts: Contact[]; total: number }> {
  const q = search.trim()
  const key = pageCacheKey(token, page, q)
  if (!opts.force) {
    const cached = pageCache.get(key)
    if (cached && Date.now() - cached.ts < PAGE_CACHE_TTL_MS) return cached.data
  }

  const url = q
    ? `${ACCOUNT_PATH}/contacts/search?q=${encodeURIComponent(q)}&page=${page}&include_contacts=true`
    : `${ACCOUNT_PATH}/contacts?page=${page}`
  const res  = await fetch(url, { headers: authHeaders(token), signal: opts.signal })
  const data = await res.json()
  if (!res.ok) throw new Error(data.message || `Erro ${res.status}`)
  const list: Contact[] = data.payload || data.data || []
  const total = data.meta?.count ?? data.meta?.contacts_count ?? list.length
  const result = { contacts: list, total }
  pageCache.set(key, { data: result, ts: Date.now() })
  return result
}

/**
 * Cache em memória do crawl completo (usado pela busca por telefone abaixo). TTL curto:
 * cobre o caso comum de digitação incremental de um número (várias buscas em sequência
 * em poucos segundos) sem servir dado velho por muito tempo. Invalidado nas mutações
 * (create/update/delete/import) pra não esconder uma alteração recente.
 */
let allContactsCache: { token: string; data: Contact[]; ts: number } | null = null
const ALL_CONTACTS_CACHE_TTL_MS = 90_000
const ALL_CONTACTS_CONCURRENCY = 5

/** Exportado só pra reset de estado de módulo entre testes (ver contactsService.test.ts). */
export function invalidateAllContactsCache() {
  allContactsCache = null
}

async function fetchPagesLimited(pages: number[], token: string, signal?: AbortSignal): Promise<Contact[][]> {
  const results: Contact[][] = new Array(pages.length)
  let next = 0
  async function worker() {
    while (next < pages.length) {
      const i = next++
      const res  = await fetch(`${ACCOUNT_PATH}/contacts?page=${pages[i]}`, { headers: authHeaders(token), signal })
      const data = await res.json()
      results[i] = (data.payload || data.data || []) as Contact[]
    }
  }
  await Promise.all(Array.from({ length: Math.min(ALL_CONTACTS_CONCURRENCY, pages.length) }, worker))
  return results
}

/**
 * Busca TODOS os contatos da conta. Usada só como suporte à busca por telefone no client
 * (ver `isPhoneLikeQuery` em `utils/contactsFormat.ts`): a busca de servidor do Chatwoot é
 * um ILIKE literal em `phone_number` e não ignora formatação nem DDI ausente, então pra
 * "achar por qualquer parte do número, com qualquer formatação" a view varre tudo e filtra
 * pelos dígitos localmente. Não usar pra listagem normal — página única existe justamente
 * pra evitar isso (ver `fetchContactsPageApi`).
 *
 * Concorrência limitada (`ALL_CONTACTS_CONCURRENCY`) em vez de todas as páginas de uma vez:
 * com ~90 páginas isso já derrubou o Chatwoot self-hosted uma vez (mesmo padrão do bug de
 * `team_members` em `auth.ts`). Cacheia o resultado por `ALL_CONTACTS_CACHE_TTL_MS` e aceita
 * um `AbortSignal` pra o chamador cancelar um crawl obsoleto (usuário ainda digitando).
 */
export async function fetchAllContactsParallelApi(token: string, signal?: AbortSignal): Promise<Contact[]> {
  const cached = allContactsCache
  if (cached && cached.token === token && Date.now() - cached.ts < ALL_CONTACTS_CACHE_TTL_MS) {
    return cached.data
  }

  const firstRes  = await fetch(`${ACCOUNT_PATH}/contacts?page=1`, { headers: authHeaders(token), signal })
  const firstData = await firstRes.json()
  if (!firstRes.ok) throw new Error(firstData.message || `Erro ${firstRes.status}`)
  const firstList: Contact[] = firstData.payload || firstData.data || []
  const total     = firstData.meta?.count ?? firstData.meta?.contacts_count ?? firstList.length
  const perPage   = firstList.length || total
  const totalPages = perPage > 0 ? Math.ceil(total / perPage) : 1

  const restPages = await fetchPagesLimited(
    Array.from({ length: Math.max(0, totalPages - 1) }, (_, i) => i + 2),
    token,
    signal
  )

  const data = [...firstList, ...restPages.flat()]
  allContactsCache = { token, data, ts: Date.now() }
  return data
}

export async function fetchContactDetailApi(token: string, id: number): Promise<Contact> {
  const res  = await fetch(`${ACCOUNT_PATH}/contacts/${id}`, { headers: authHeaders(token) })
  const data = await res.json()
  if (!res.ok) throw new Error(data.message || `Erro ${res.status}`)
  return data.payload || data
}

/** Labels de um único contato. Não confundir com o batch de `templatesService.fetchContactLabelsApi`. */
export async function fetchContactLabelsApi(token: string, id: number): Promise<string[]> {
  const res  = await fetch(`${ACCOUNT_PATH}/contacts/${id}/labels`, { headers: authHeaders(token) })
  const data = await res.json()
  return data.payload || []
}

export async function fetchContactConversationsApi(token: string, id: number): Promise<any[]> {
  const res  = await fetch(`${ACCOUNT_PATH}/contacts/${id}/conversations`, { headers: authHeaders(token) })
  const data = await res.json()
  return ((data.payload || []) as any[]).sort((a, b) => b.id - a.id)
}

/** Busca global de mensagens (usada pela busca dentro do histórico de conversas do contato). */
export async function searchMessagesApi(token: string, query: string): Promise<any[]> {
  const url  = `${ACCOUNT_PATH}/search?q=${encodeURIComponent(query)}&page=1&include_contacts=true`
  const res  = await fetch(url, { headers: authHeaders(token) })
  const data = await res.json()
  return data?.payload?.messages ?? []
}

/**
 * Busca todas as mensagens de uma conversa para exportação: primeira página + paginação
 * retroativa (`before=`) até 50 iterações, com dedup por Set de ids já vistos. Auto-contido,
 * não toca estado reativo da view.
 */
export async function fetchConversationMessagesForExportApi(token: string, convId: number): Promise<any[]> {
  const hdrs = authHeaders(token)
  const base = `${ACCOUNT_PATH}/conversations/${convId}/messages`

  const firstRes = await fetch(base, { headers: hdrs })
  if (!firstRes.ok) throw new Error(`Erro ${firstRes.status}`)
  let messages: any[] = (await firstRes.json()).payload || []

  for (let i = 0; i < 50; i++) {
    if (!messages.length) break
    const oldestId = Math.min(...messages.map((m: any) => m.id))
    const res2 = await fetch(`${base}?before=${oldestId}`, { headers: hdrs })
    if (!res2.ok) break
    const older: any[] = (await res2.json()).payload || []
    if (!older.length) break
    const seen = new Set(messages.map((m: any) => m.id))
    const fresh = older.filter((m: any) => !seen.has(m.id))
    if (!fresh.length) break
    messages = [...fresh, ...messages]
  }

  messages.sort((a, b) => a.created_at - b.created_at)
  return messages
}

export async function createContactApi(token: string, body: Record<string, unknown>): Promise<Contact> {
  const res  = await fetch(`${ACCOUNT_PATH}/contacts`, { method: 'POST', headers: jsonHeaders(token), body: JSON.stringify(body) })
  const data = await res.json()
  if (!res.ok) throw new Error(data.message || `Erro ${res.status}`)
  invalidateAllContactsCache()
  invalidatePageCache()
  return data.payload || data
}

export async function updateContactApi(token: string, id: number, body: Record<string, unknown>): Promise<Contact> {
  const res  = await fetch(`${ACCOUNT_PATH}/contacts/${id}`, { method: 'PATCH', headers: jsonHeaders(token), body: JSON.stringify(body) })
  const data = await res.json()
  if (!res.ok) throw new Error(data.message || `Erro ${res.status}`)
  invalidateAllContactsCache()
  invalidatePageCache()
  return data.payload || data
}

export async function deleteContactApi(token: string, id: number): Promise<void> {
  const res = await fetch(`${ACCOUNT_PATH}/contacts/${id}`, { method: 'DELETE', headers: authHeaders(token) })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.message || `Erro ${res.status}`)
  }
  invalidateAllContactsCache()
  invalidatePageCache()
}

export async function importContactsCsvApi(token: string, file: File): Promise<{ message: string }> {
  const fd = new FormData()
  fd.append('import[file]', file)
  const res  = await fetch(`${ACCOUNT_PATH}/contacts/import`, { method: 'POST', headers: authHeaders(token), body: fd })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.message || `Erro ${res.status}`)
  invalidateAllContactsCache()
  invalidatePageCache()
  return data
}

/**
 * Catálogo de labels da conta, forma bruta (`AccountLabel`). Endpoint idêntico ao de
 * `templatesService.fetchAccountLabelsApi`, mas forma de retorno diferente (lá é `CwLabel`
 * mapeado) — não reaproveitada de propósito.
 */
export async function fetchAccountLabelsApi(token: string): Promise<AccountLabel[]> {
  const res  = await fetch(`${ACCOUNT_PATH}/labels`, { headers: authHeaders(token) })
  const data = await res.json()
  return data.payload || []
}

export async function saveContactLabelsApi(token: string, contactId: number, labels: string[]): Promise<string[]> {
  const res  = await fetch(`${ACCOUNT_PATH}/contacts/${contactId}/labels`, {
    method: 'POST',
    headers: jsonHeaders(token),
    body: JSON.stringify({ labels }),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.message || `Erro ${res.status}`)
  return data.payload || labels
}
